/**
 * Build a WebP srcset for product images stored in Supabase Storage.
 *
 * Originals live at e.g.
 *   …/product-images/seed/<name>.jpg
 *   …/product-images/hover/<name>.jpg
 *
 * The optimize-product-images script writes WebP variants alongside them:
 *   …/product-images/seed/<name>-400.webp
 *   …/product-images/seed/<name>-600.webp
 *   …/product-images/seed/<name>-900.webp
 *
 * If the URL doesn't match the expected pattern we return undefined so callers
 * fall back to the original <img src>.
 */
const WIDTHS = [400, 600, 900] as const;

export function productImageWebpSrcSet(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const match = url.match(/^(.*\/product-images\/(?:seed|hover)\/[^/?#]+)\.(?:jpe?g|png)(\?.*)?$/i);
  if (!match) return undefined;
  const base = match[1];
  return WIDTHS.map((w) => `${base}-${w}.webp ${w}w`).join(", ");
}

/**
 * Standard sizes attribute for the product card grid.
 * PLPs render md:grid-cols-4 — at 1280px each card is ~300px CSS, so
 * 380px overstates and forces the 900w variant unnecessarily at 2x DPR.
 * Tighter hint lets the browser pick 600w on standard retina laptops.
 */
export const PRODUCT_CARD_SIZES =
  "(min-width: 1280px) 300px, (min-width: 768px) 24vw, 48vw";

/**
 * sizes attribute for the homepage 6-up category tile grid (grid-cols-2 md:grid-cols-3).
 * At md+ each tile is ~33vw; on mobile a 2-col grid with 20px gap is ~48vw, not 50vw.
 */
export const CATEGORY_TILE_SIZES =
  "(min-width: 1024px) 380px, (min-width: 768px) 33vw, 48vw";
